import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Briefcase, MapPin, CalendarDays, ChevronRight, Wallet,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SectionRibbon } from "@/components/AppShell";
import RowMenu from "@/components/RowMenu";
import MoveDialog from "@/components/MoveDialog";
import { toast } from "sonner";

const TIDS = {
  page: "business-chapter-page",
  trip: (id) => `business-trip-${id}`,
  tripMenu: (id) => `business-trip-menu-${id}`,
  empty: "business-chapter-empty",
};

const inp =
  "w-full px-3 py-2 rounded-lg bg-tb-cream-2 border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function TripEditDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial.name || "",
    destination_name: initial.destination_name || "",
    country: initial.country || "",
    start_date: initial.start_date || "",
    end_date: initial.end_date || "",
    total_budget: String(initial.total_budget || ""),
    notes: initial.notes || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/booked-trips/${initial.id}`, {
        ...form,
        total_budget: parseFloat(form.total_budget) || 0,
        trip_type: "business",
        destination_id: initial.destination_id || null,
        image: initial.image || "",
      });
      toast.success("Trip updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-3">
      <form onSubmit={submit} className="bg-tb-cream rounded-2xl border-2 border-tb-gold max-w-md w-full">
        <div className="bg-tb-navy text-tb-gold-bright px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <span className="font-hand text-xl">edit business trip</span>
          <button type="button" onClick={onClose} className="font-hand text-tb-gold tb-tap">close</button>
        </div>
        <div className="p-4 space-y-3">
          <input className={inp} placeholder="Trip name" value={form.name}
            onChange={(e) => set("name", e.target.value)} required />
          <div className="grid grid-cols-2 gap-2">
            <input className={inp} placeholder="Destination" value={form.destination_name}
              onChange={(e) => set("destination_name", e.target.value)} />
            <input className={inp} placeholder="Country" value={form.country}
              onChange={(e) => set("country", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="font-hand text-tb-navy/65 text-xs ml-1">start</span>
              <input type="date" className={inp} value={form.start_date}
                onChange={(e) => set("start_date", e.target.value)} />
            </label>
            <label className="block">
              <span className="font-hand text-tb-navy/65 text-xs ml-1">end</span>
              <input type="date" className={inp} value={form.end_date}
                onChange={(e) => set("end_date", e.target.value)} />
            </label>
          </div>
          <label className="block">
            <span className="font-hand text-tb-navy/65 text-xs ml-1">total budget (USD)</span>
            <input className={inp} inputMode="decimal" value={form.total_budget}
              onChange={(e) => set("total_budget", e.target.value.replace(/[^\d.]/g, ""))} />
          </label>
          <button className="w-full py-2.5 rounded-xl bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-lg tb-tap">
            save changes
          </button>
        </div>
      </form>
    </div>
  );
}

export default function BusinessChapterPage() {
  const nav = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTrip, setEditingTrip] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/business-trips");
      setTrips(data.trips || []);
    } catch (err) {
      console.error("[Business] load failed:", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const removeTrip = async (t) => {
    if (!window.confirm(`Delete the trip "${t.name}"? This can't be undone.`)) return;
    try { await api.delete(`/booked-trips/${t.id}`); toast.success("Trip deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const moveTrip = async (payload) => {
    try {
      await api.patch(`/booked-trips/${moveTarget.id}/move`, payload);
      const target = payload.trip_type === "business" ? "business" : `chapter ${payload.year}`;
      toast.success(`Moved to ${target}`);
      setMoveTarget(null);
      if (payload.trip_type === "business") load();
      else nav(`/chapters/${payload.year}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't move");
    }
  };

  return (
    <div data-testid={TIDS.page} className="flex flex-col">
      <SectionRibbon>business, not pleasure</SectionRibbon>

      <div className="px-5 pt-3 pb-2 flex items-center justify-between gap-2">
        <button
          onClick={() => nav("/chapters")}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 font-hand text-tb-navy/70 tb-tap text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> chapters
        </button>
        <span className="font-hand text-tb-navy/60 italic text-sm">
          {trips.length} business trip{trips.length === 1 ? "" : "s"}
        </span>
      </div>

      <p className="px-5 font-hand text-tb-navy/60 italic text-sm">
        Flights & hotels booked for work — kept out of your travel journal.
      </p>

      <div className="px-5 pt-3 pb-10">
        {loading ? (
          <p className="font-hand text-tb-navy/55 text-center py-10">~ loading ~</p>
        ) : trips.length === 0 ? (
          <div data-testid={TIDS.empty} className="text-center py-12 px-4">
            <Briefcase className="w-12 h-12 mx-auto text-tb-navy/30 mb-2" />
            <p className="font-serif-it text-tb-navy text-2xl">no business trips yet</p>
            <p className="font-hand text-tb-navy/60 mt-2">
              When you book a hotel or flight strictly for work, choose
              "add to business" on the booking dialog and it'll live here —
              separate from your chapters.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {trips.map((t) => {
              const totalBudget = parseFloat(t.total_budget) || 0;
              return (
                <li key={t.id}>
                  <div className="rounded-2xl bg-tb-cream border-2 border-tb-cream-3 hover:border-tb-navy p-3 flex items-start gap-2">
                    <button
                      data-testid={TIDS.trip(t.id)}
                      onClick={() => nav(`/booked/${t.id}`)}
                      className="flex-1 text-left tb-tap"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-serif-it text-tb-navy text-xl truncate flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-tb-navy/70" />
                            {t.name}
                          </h3>
                          <p className="font-hand text-tb-navy/65 text-sm flex items-center gap-1 mt-0.5 truncate">
                            <MapPin className="w-3.5 h-3.5" />
                            {[t.destination_name, t.country].filter(Boolean).join(", ") || "—"}
                          </p>
                          <p className="font-hand text-tb-navy/60 text-xs flex items-center gap-1 mt-0.5">
                            <CalendarDays className="w-3 h-3" />
                            {fmtDate(t.start_date)}{t.end_date && ` → ${fmtDate(t.end_date)}`}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-tb-navy/40 shrink-0" />
                      </div>
                      {totalBudget > 0 && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-tb-navy/70">
                          <Wallet className="w-3.5 h-3.5 text-tb-orange" />
                          <span className="font-hand">
                            ${Math.round(t.spent || 0).toLocaleString()} / $
                            {Math.round(totalBudget).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </button>
                    <RowMenu
                      testId={TIDS.tripMenu(t.id)}
                      onEdit={() => setEditingTrip(t)}
                      onMove={() => setMoveTarget(t)}
                      onDelete={() => removeTrip(t)}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {editingTrip && (
        <TripEditDialog
          initial={editingTrip}
          onClose={() => setEditingTrip(null)}
          onSaved={() => { setEditingTrip(null); load(); }}
        />
      )}
      {moveTarget && (
        <MoveDialog
          kind="trip"
          current={{
            year: parseInt((moveTarget.start_date || "").slice(0, 4), 10) || new Date().getFullYear(),
            trip_type: "business",
          }}
          onClose={() => setMoveTarget(null)}
          onConfirm={moveTrip}
        />
      )}
    </div>
  );
}
